/**
 * Polling as a Lit reactive controller.
 *
 * The plane never presumes the outcome of an action: what a page shows is what the clawlets
 * reported, and that report arrives later, on its own. A page therefore reloads what it shows at
 * a fixed interval while it is connected and the tab is visible, and right away on demand (after
 * an action, so the first change shows without waiting for the next tick).
 *
 * Anything that changes the data behind every page at once (the demo controls, notably) asks all
 * of them to reload by dispatching {@link REFRESH_EVENT} on `document.body`.
 *
 * The host reads `value`, `error` and `loading`; a failed reload keeps the last good value, so a
 * transient error does not blank a page that was showing data a second ago.
 */
export const REFRESH_EVENT = 'poll/refresh';

export class PollController {
  /**
   * @param host the Lit element to re-render
   * @param load async function returning the value to show
   * @param intervalMs interval between loads; 0 loads once
   */
  constructor(host, load, intervalMs) {
    this.host = host;
    this.load = load;
    this.intervalMs = intervalMs;
    this.value = undefined;
    this.error = null;
    this.loading = false;
    this.timer = null;
    this.generation = 0;
    this.onVisibility = () => {
      if (document.visibilityState === 'visible') {
        this.refresh();
      }
    };
    this.onRefresh = () => this.refresh();
    host.addController(this);
  }

  hostConnected() {
    document.addEventListener('visibilitychange', this.onVisibility);
    document.body.addEventListener(REFRESH_EVENT, this.onRefresh);
    this.refresh();
  }

  hostDisconnected() {
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.body.removeEventListener(REFRESH_EVENT, this.onRefresh);
    clearTimeout(this.timer);
    this.timer = null;
    // a load still in flight must not schedule the next one
    this.generation++;
  }

  /** Forgets the current value, e.g. when the host now shows another resource. */
  reset() {
    this.value = undefined;
    this.error = null;
    this.refresh();
  }

  /** Loads now, and restarts the interval from here. */
  async refresh() {
    clearTimeout(this.timer);
    const generation = ++this.generation;
    this.loading = true;
    this.host.requestUpdate();
    try {
      const value = await this.load();
      if (generation !== this.generation) {
        return;
      }
      this.value = value;
      this.error = null;
    } catch (error) {
      if (generation !== this.generation) {
        return;
      }
      this.error = error;
    }
    this.loading = false;
    this.host.requestUpdate();
    if (this.intervalMs > 0 && document.visibilityState !== 'hidden') {
      this.timer = setTimeout(() => this.refresh(), this.intervalMs);
    }
  }
}
